import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import api from '../../api';
import {
  User,
  UserPreferences,
  ViewSelectProp,
} from '../../models/User';
import PopNotificationsContext from '../../providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import CurrentUserContext, { CallbackConfig } from './CurrentUser.context';
import utils, {
  checkIsUserSuperAdmin,
  checkIsUltraAdmin,
  getIsListingOwner,
  isEnterpriseRWC,
} from 'utils';
import { SUCCESS_MESSAGES } from 'request-responses';
import FourDotLoader from 'components/FourDotLoader';
import {
  Listing,
  STATUSES_TO_ENQUIRE,
  STATUSES_TO_WATCH,
} from 'models/Listing';
import NeedEmailVerificationModal from 'components/NeedEmailVerificationModal';
import useVerification from 'hooks/useVerification';
import EmailMissingModal from 'components/EmailMissingModal';
import ReminderToVerifyPhoneNumberModal from 'components/ReminderToVerifyPhoneNumberModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useFirst from 'hooks/useFirst';
import credentialsService from 'services/credentialsService';
import PhoneMissingModal from 'components/PhoneMissingModal';
import PhoneVerificationModal from 'components/PhoneVerificationModal';
import { useLocation } from 'react-router-dom';
import {
  DATA_ROOM_ROUTE,
  OPEN_ENQUIRY_HASH,
  SHAREABLE_LINKS_ROUTE,
} from 'data/global';
import CurrentUserEnquiryContext from 'providers/CurrentUserEnquiryContext';
import useCurrentUserMessagingCount from './hooks/useCurrentUserMessagingCount';
import { UpdatePasswordValues } from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/hooks/useProfileForm';

const PROFILE_PICTURE = 'profileImage';

const ROUTES_TO_EXCLUDE_FOR_VERIF_REMINDER = [
  SHAREABLE_LINKS_ROUTE,
  DATA_ROOM_ROUTE,
];

export const multiSelectKeys: Array<keyof User> = [
  'preferredIncludeIndustries',
  'preferredExcludeIndustries',
  'preferredLocations',
];

type CurrentUserProps = {
  currentUser: User;
  setCurrentUser: Dispatch<SetStateAction<User>>;
  refetchCurrentUser: () => Promise<void>;
};

const CurrentUser: React.FC<CurrentUserProps> = (props) => {
  const { children, currentUser, setCurrentUser, refetchCurrentUser } = props;

  const [
    reminderToVerifyPhoneNumberModal,
    reminderToVerifyPhoneNumberModalRef,
  ] = useCallbackRef<ModalRef>();

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { pathname, hash } = useLocation();

  const { isFirst, onFirst } = useFirst();

  const currentUserId = currentUser?._id;

  const isSuperAdmin = useMemo(
    () => checkIsUserSuperAdmin(currentUser?.role),
    [currentUser],
  );

  const isUltraAdmin = useMemo(
    () => checkIsUltraAdmin(currentUser?.role),
    [currentUser],
  );

  const {
    hasEnterprise,
    isEnterpriseHead,
    isOfficeManager,
    isRegularEnterpriseMember,
    enterpriseMember,
    enterprise,
    isRWCMember,
    hasEnterpriseLogo,
  } = useMemo(() => {
    const enterpriseMember = currentUser?.enterpriseMember;

    const {
      isEnterpriseHead,
      isOfficeManager: propIsOfficeManager,
      enterprise,
    } = enterpriseMember || {};

    const isOfficeManager = !isEnterpriseHead && propIsOfficeManager;

    return {
      enterpriseMember,
      isEnterpriseHead,
      isOfficeManager,
      // Enterprise member that is neither an enterprise head nor an office
      // manager.
      isRegularEnterpriseMember:
        !!enterpriseMember && !isEnterpriseHead && !isOfficeManager,
      enterprise,
      hasEnterprise: !!enterpriseMember,
      hasEnterpriseLogo: !!enterprise?.logo || !!enterprise?.mobileLogo,
      isRWCMember: isEnterpriseRWC(enterprise),
    };
  }, [currentUser?.enterpriseMember]);

  const {
    isUserVerified,
    enquireListingId,
    phoneVerificationModal,
    emailMissingModalRef,
    needEmailVerificationModalRef,
    phoneMissingModalRef,
    phoneVerificationModalRef,
    openVerificationModal,
    openPhoneVerificationModal,
  } = useVerification(currentUser);

  const { t } = useTranslation();

  const updateCurrentUser = useCallback(
    (user: Partial<User>) => setCurrentUser((prev) => ({ ...prev, ...user })),
    [setCurrentUser],
  );

  const changePassword = useCallback(
    async (
      value: UpdatePasswordValues,
      { onSuccess, onError }: CallbackConfig,
    ) => {
      const { oldPassword, newPassword } = value;
      try {
        await api.users.changePassword(oldPassword, newPassword);
        onSuccess?.();
        popSuccess(t(SUCCESS_MESSAGES.CHAGED_PASSWORD));
      } catch (e) {
        onError?.();
        popServerError(e);
      }
    },
    [popSuccess, popServerError, t],
  );

  const patchCurrentUser = useCallback(
    async (
      value: Partial<User>,
      { skipToast, skipErrorToast, onSuccess }: CallbackConfig = {
        skipToast: false,
        skipErrorToast: false,
        onSuccess: utils.noop,
      },
    ) => {
      if (!currentUserId) return;

      const patchedValues = (
        Object.entries(value) as Array<[keyof User, User[keyof User]]>
      ).reduce((acc, [key, value]) => {
        let patchVal;
        if (multiSelectKeys.includes(key)) {
          patchVal = utils.multiSelectValueToStringArray(
            value as Array<Record<string, any> & { value: string }>,
          );
        } else {
          patchVal = value;
        }
        return { ...acc, [key]: patchVal };
      }, {} as Partial<User>);

      try {
        const {
          data: { user },
        } = await api.users.patchUser(currentUserId, patchedValues);
        // not using updateCurrentUser just in case a field is expected to be cleared by a patch
        setCurrentUser(user);
        onSuccess?.();

        if (!skipToast) popSuccess(t(SUCCESS_MESSAGES.PACHED_USER));
      } catch (e) {
        if (!skipErrorToast) popServerError(e);
      }
    },
    [currentUserId, setCurrentUser, popSuccess, popServerError, t],
  );

  const updatePreferences = useCallback(
    (preferences: Partial<UserPreferences>) => {
      api.users.updatePreferences(preferences);
      updateCurrentUser(preferences);
    },
    [updateCurrentUser],
  );

  const uploadProfileImage = useCallback(
    async (value: File) => {
      if (!value) return;
      try {
        const profileImage = await api.users.uploadFile(
          currentUserId,
          value,
          PROFILE_PICTURE,
        );
        popSuccess(t(SUCCESS_MESSAGES.IMAGE_UPLOADED));
        updateCurrentUser({ profileImage });
        return profileImage;
      } catch (e) {
        popServerError(e);
      }
    },
    [currentUserId, popSuccess, t, updateCurrentUser, popServerError],
  );

  const deleteProfileImage = useCallback(async () => {
    try {
      const {
        data: { fileId },
      } = await api.users.deleteFile(
        currentUserId,
        PROFILE_PICTURE,
        currentUser?.profileImage?.id,
      );
      updateCurrentUser({ profileImage: null });
      popSuccess(t(SUCCESS_MESSAGES.IMAGE_DELETED));
      return fileId;
    } catch (e) {
      popServerError(e);
    }
  }, [
    currentUserId,
    currentUser?.profileImage?.id,
    updateCurrentUser,
    popSuccess,
    t,
    popServerError,
  ]);

  const isListingEnquirableAndWatchableByMe = useCallback(
    (listing: Listing): [boolean, boolean] => {
      if (!listing || isSuperAdmin) return [false, false];

      const { userId, isEnquired, stage, isCoOwner } = listing;

      const isListingOwner = getIsListingOwner(
        currentUserId,
        userId,
        isCoOwner,
      );

      if (isListingOwner) return [false, false];

      const isEnquirable = !isEnquired && STATUSES_TO_ENQUIRE.includes(stage);

      const isWatchlistable = STATUSES_TO_WATCH.includes(stage);

      return [isEnquirable, isWatchlistable];
    },
    [currentUserId, isSuperAdmin],
  );

  const updateView = useCallback(
    (key: ViewSelectProp, value: boolean) => {
      try {
        api.users.patchUser(currentUserId, { [key]: value });
        updateCurrentUser({ [key]: value });
      } catch (e) {
        return;
      }
    },
    [currentUserId, updateCurrentUser],
  );

  const { markSessionRead, markSessionUnread } =
    useCurrentUserMessagingCount(setCurrentUser);

  useEffect(() => {
    if (isFirst && reminderToVerifyPhoneNumberModal && currentUser) {
      onFirst(() => {
        const { hasRemindedToVerifyPhone, hasVerifiedContactNumber } =
          currentUser;

        if (!hasRemindedToVerifyPhone && !hasVerifiedContactNumber) {
          if (
            credentialsService.hasJustRegistered ||
            hash === OPEN_ENQUIRY_HASH ||
            ROUTES_TO_EXCLUDE_FOR_VERIF_REMINDER.some((route) =>
              pathname.startsWith(route),
            )
          ) {
            return;
          }

          reminderToVerifyPhoneNumberModal?.open();
        }
      });
    }
  }, [
    onFirst,
    currentUser,
    isFirst,
    pathname,
    reminderToVerifyPhoneNumberModal,
    hash,
  ]);

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        isUserVerified,
        isSuperAdmin,
        isUltraAdmin,
        hasEnterprise,
        isEnterpriseHead,
        isOfficeManager,
        isRegularEnterpriseMember,
        isRWCMember,
        enterpriseMember,
        enterprise,
        hasEnterpriseLogo,
        refetchCurrentUser,
        updateCurrentUser,
        openVerificationModal,
        openPhoneVerificationModal,
        changePassword,
        patchCurrentUser,
        updatePreferences,
        uploadProfileImage,
        deleteProfileImage,
        markSessionRead,
        markSessionUnread,
        isListingEnquirableAndWatchableByMe,
        updateView,
      }}
    >
      {!currentUser ? (
        <FourDotLoader />
      ) : (
        <CurrentUserEnquiryContext>{children}</CurrentUserEnquiryContext>
      )}

      <ReminderToVerifyPhoneNumberModal
        ref={reminderToVerifyPhoneNumberModalRef}
        onVerifyClick={openPhoneVerificationModal}
      />
      <PhoneMissingModal
        ref={phoneMissingModalRef}
        onUserUpdate={updateCurrentUser}
        onSuccess={phoneVerificationModal?.open}
      />
      <PhoneVerificationModal ref={phoneVerificationModalRef} />

      <EmailMissingModal
        ref={emailMissingModalRef}
        listingId={enquireListingId}
        onUserUpdate={updateCurrentUser}
      />
      <NeedEmailVerificationModal
        ref={needEmailVerificationModalRef}
        listingId={enquireListingId}
      />

    </CurrentUserContext.Provider>
  );
};

export default CurrentUser;
